import { z } from 'zod'
import { ShareSnsDataSchema } from './share-sns'

const SpotInStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
})

export const StampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
  spots: z.array(SpotInStampDataSchema).optional().default([]),
})

export const StampQuizDataSchema = z.object({
  id: z.number(),
  stampId: z.number().nullable(),
  orderNumber: z.number().nullable(),
  title: z.string().nullable(),
  question: z.string().nullable(),
  status: z.number(),
})

/** クイズ選択肢スキーマ */
const StampQuizChoiceSchema = z.object({
  id: z.number(),
  item: z.string().nullable(),
})

/**
 * 選択肢付きクイズ詳細スキーマ
 * GET /api/v2/stamps/{id}/quizzes のレスポンスに使用する
 */
export const StampQuizDetailSchema = z.object({
  id: z.number(),
  stampId: z.number().nullable(),
  orderNumber: z.number().nullable(),
  title: z.string().nullable(),
  question: z.string().nullable(),
  questionImage: z.string().nullable().optional(),
  quizType: z.number(),
  /**
   * 入力式クイズの正解の文字数（前後の空白を除く・コードポイント単位）。#2591
   * 選択式・正解未設定のときは null。旧レスポンス互換のためフィールド欠落も許容する。
   * 正解そのものは返らない。
   */
  answerLength: z.number().int().nullable().optional(),
  dailyLimitMode: z.number().default(0),
  status: z.number(),
  choices: z.array(StampQuizChoiceSchema).default([]),
})

/** スタンプ獲得済みレコード（POST /api/v2/stamps/{id} レスポンス内 stamps[]） */
export const UserStampAcquireDataSchema = z.object({
  id: z.number(),
  stampId: z.number().nullable(),
  userRound: z.number().nullable(),
  periodId: z.number().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  /** 獲得したスタンプ名。旧レスポンス互換のためフィールド欠落を許容する（#2540） */
  name: z.string().nullable().optional(),
  /** 獲得したスタンプの画像URL（署名付き）。複数同時獲得時に1枚ずつ正しい絵を出すために使う（#2540） */
  image: z.string().nullable().optional(),
  /** このスタンプで付与されたポイント数（付与時に確定した実値）。ポイントラリーの獲得ダイアログで表示する（#2540） */
  point: z.number().optional(),
})

/** 獲得特典データ（POST /api/v2/stamps/{id} レスポンス内 acquiredRewards[]） */
export const AcquiredRewardDataSchema = z.object({
  rewardId: z.number(),
  rewardName: z.string(),
})

export const StampGetResponseSchema = z.object({
  stamp: StampDataSchema.nullable(),
})

export const StampListResponseSchema = z.object({
  stamps: z.array(StampDataSchema),
})

export const StampQuizResponseSchema = z.object({
  quizzes: z.array(StampQuizDataSchema),
})

/**
 * GET /api/v2/stamps/{id}/quizzes のレスポンス（選択肢付き）
 */
export const StampQuizListResponseSchema = z.object({
  quizzes: z.array(StampQuizDetailSchema),
})

/**
 * POST /api/v2/stamps/{id} のレスポンス
 */
export const StampAcquireResponseSchema = z.object({
  stamps: z.array(UserStampAcquireDataSchema),
  acquiredRewards: z.array(AcquiredRewardDataSchema),
  /** 拡散用SNS（#3285）。SNS拡散が有効なスタンプ獲得時のみ enabled=true */
  shareSns: ShareSnsDataSchema.nullable().optional(),
})

/** POST /api/v2/stamps/{stampId}/quizzes/{quizId}/answer レスポンス内 quiz */
export const QuizAnswerDataSchema = z.object({
  quizId: z.number(),
  isCorrect: z.boolean(),
  answer: z.string().nullable(),
  message: z.string().nullable(),
  hintMessage: z.string().nullable(),
  correctImage: z.string().nullable(),
  incorrectImage: z.string().nullable(),
  retry: z.boolean(),
})

/** POST /api/v2/stamps/{stampId}/quizzes/{quizId}/answer レスポンス */
export const QuizAnswerResponseSchema = z.object({
  quiz: QuizAnswerDataSchema,
})

export type StampData = z.infer<typeof StampDataSchema>
export type StampQuizData = z.infer<typeof StampQuizDataSchema>
export type StampQuizDetail = z.infer<typeof StampQuizDetailSchema>
export type UserStampAcquireData = z.infer<typeof UserStampAcquireDataSchema>
export type AcquiredRewardData = z.infer<typeof AcquiredRewardDataSchema>
export type StampGetResponse = z.infer<typeof StampGetResponseSchema>
export type StampListResponse = z.infer<typeof StampListResponseSchema>
export type StampQuizResponse = z.infer<typeof StampQuizResponseSchema>
export type StampQuizListResponse = z.infer<typeof StampQuizListResponseSchema>
export type StampAcquireResponse = z.infer<typeof StampAcquireResponseSchema>
export type QuizAnswerData = z.infer<typeof QuizAnswerDataSchema>
export type QuizAnswerResponse = z.infer<typeof QuizAnswerResponseSchema>
